import { useLingui } from '@lingui/react/macro';

/** A screenshot per locale. Typing `en` as required makes the fallback impossible to forget. */
export type LocalizedSrc = Record<string, string> & { en: string };

/**
 * A panel holds four slots, and the hand-off fills them differently per channel: `Tiendas` and
 * `Ecommerce` are four numbered steps, while `En nómina` and `Facturas` alternate two steps with two
 * photos, and `Portal Solidario` is four photos with no steps at all.
 */
export type SolutionCard =
	/**
	 * `art` is the hand-drawn illustration for the step: an SVG under `public/redesign/steps`, exported
	 * from the hand-off. Each drawing has its own canvas (168x182, 193x193, 212x98, …) and is fitted to
	 * the card's slot, so they are NOT interchangeable in size — see `StepCard`.
	 */
	| { kind: 'step'; art: string; title: string; description: string }
	/** A photograph filling the whole slot. */
	| { kind: 'media'; src: string; alt: string }
	/** Product interface sitting on a dark device frame, as the hand-off draws the portal screens. */
	| { kind: 'screen'; src: LocalizedSrc; alt: string };

/**
 * The five channels, in the hand-off's order.
 *
 * A closed list because it is now part of the URL: the Solutions page reads `?solution=` to open a tab
 * directly, which is what lets the footer's "Solidarity euro", "Solidarity portals" and "Payroll
 * rounding" links land on the section they name instead of on the top of the page (TEC-3821 B4). The
 * first entry is the tab the page opens with.
 */
export const SOLUTION_KEYS = ['stores', 'ecommerce', 'payroll', 'invoices', 'portal'] as const;

export type SolutionKey = (typeof SOLUTION_KEYS)[number];

export type Solution = {
	key: SolutionKey;
	/** Tab label. The hand-off names the CHANNEL the donation travels through, not the product. */
	tab: string;
	description: string;
	cta?: { label: string };
	cards: SolutionCard[];
};

/**
 * The portal's dark card, composited rather than exported whole: Figma delivers the two white panels
 * separately, so each was placed onto the ink frame at the hand-off's coordinates — (53, 40) and
 * (53, 188.95) of a 309.25x391 card — and flattened at 2x. Re-run it if the designers move a panel.
 */
const PORTAL_IMPACT: LocalizedSrc = {
	ca: '/redesign/portal/impact-ca.png',
	en: '/redesign/portal/impact-en.png',
	es: '/redesign/portal/impact-es.png',
	fr: '/redesign/portal/impact-fr.png',
	it: '/redesign/portal/impact-it.png',
};

/**
 * A live campaign page, inset at (39, 45) of the ink card and overflowing its right and bottom edges.
 *
 * SHIPPED SHARP ON PURPOSE: the hand-off blurs this card, but next to the pin-sharp impact card beside
 * it any blur read as a rendering fault, so the team asked for it out. Don't "restore" it to match
 * Figma without asking. Encoded 4:4:4, since subsampling fringes the small blue interface text.
 */
const PORTAL_PAGE: LocalizedSrc = {
	ca: '/redesign/portal/page-ca.jpg',
	en: '/redesign/portal/page-en.jpg',
	es: '/redesign/portal/page-es.jpg',
	fr: '/redesign/portal/page-fr.jpg',
	it: '/redesign/portal/page-it.jpg',
};

/**
 * Step artwork, named after what each drawing SHOWS rather than after a channel and step number: half
 * are reused across channels, so a `stores-04-…` name would be wrong the first time it was borrowed.
 * Reused ones are stored once, even though Figma exports them per channel at different scales.
 */
const ART = {
	browserHeart: '/redesign/steps/browser-heart.svg',
	browserHeartBranches: '/redesign/steps/browser-heart-branches.svg',
	browserPhoneHeart: '/redesign/steps/browser-phone-heart.svg',
	causeCard: '/redesign/steps/cause-card.svg',
	phoneConfirmation: '/redesign/steps/phone-confirmation.svg',
	receiptCode: '/redesign/steps/receipt-code.svg',
	terminalAccepted: '/redesign/steps/terminal-accepted.svg',
	terminalPrompt: '/redesign/steps/terminal-prompt.svg',
} as const;

/**
 * A hook rather than a module constant because `t` is a Lingui macro: it only extracts, and only
 * re-renders on a language change, when called from inside a component tree.
 */
export function useSolutions(): Solution[] {
	const { t } = useLingui();

	// `En nómina` and `Facturas` share these two steps in the hand-off, word for word.
	const radicalSimplicity: SolutionCard = {
		kind: 'step',
		art: ART.browserHeart,
		title: t`Radical simplicity`,
		description: t`The user decides once and becomes a recurring donor. It is the "micro-donation" that generates a macro-impact.`,
	};
	const totalTransparency: SolutionCard = {
		kind: 'step',
		art: ART.browserHeartBranches,
		title: t`Total transparency`,
		description: t`100% traceability of every cent. We automate the sending of tax certificates and the project updates that keep them motivated.`,
	};

	return [
		{
			key: 'stores',
			tab: t`Stores`,
			description: t`When paying by card, the customer adds a few cents to their purchase and donates them to a social cause. Straight from the card terminal, no friction.`,
			cta: { label: t`Access the guide` },
			cards: [
				{
					kind: 'step',
					art: ART.terminalPrompt,
					title: t`Proposal`,
					description: t`When paying by card, the terminal shows a message offering to round the purchase up for a social project.`,
				},
				{
					kind: 'step',
					art: ART.terminalAccepted,
					title: t`Acceptance`,
					description: t`The shopper presses the green button to accept and inserts or taps their card.`,
				},
				{
					kind: 'step',
					art: ART.receiptCode,
					title: t`Receipt`,
					description: t`Once the transaction is approved, a donation receipt is printed with a unique, personal code.`,
				},
				{
					kind: 'step',
					art: ART.browserPhoneHeart,
					title: t`Transparency`,
					description: t`The customer enters the code at app.worldcoo.com to see the project and download their tax certificate.`,
				},
			],
		},
		{
			key: 'ecommerce',
			tab: t`Ecommerce`,
			description: t`The donation is built into your online store's payment. One click before completing the order, without interrupting the purchase.`,
			cards: [
				{
					kind: 'step',
					art: ART.browserHeart,
					title: t`Proposal`,
					description: t`At the checkout, the option to add a fixed amount appears (0.50 €, 1 €…).`,
				},
				{
					kind: 'step',
					art: ART.causeCard,
					title: t`Information`,
					description: t`A short description and an image of the cause are shown to motivate the shopper.`,
				},
				{
					kind: 'step',
					art: ART.phoneConfirmation,
					title: t`Confirmation`,
					description: t`The donation code appears on the thank-you page and is also sent by email.`,
				},
				{
					kind: 'step',
					art: ART.browserPhoneHeart,
					title: t`Certificate`,
					description: t`The customer manages their tax certificate on their own on our platform, app.worldcoo.com.`,
				},
			],
		},
		{
			key: 'payroll',
			tab: t`On payroll`,
			description: t`Every month, employees can add a microdonation to their payslip: a round-up or a fixed amount.`,
			cards: [
				radicalSimplicity,
				{ kind: 'media', src: '/redesign/payroll-desk.jpg', alt: t`An employee going through her payslip at her desk` },
				totalTransparency,
				{ kind: 'media', src: '/redesign/payroll-team.jpg', alt: t`Colleagues taking notes around a table` },
			],
		},
		{
			key: 'invoices',
			tab: t`Invoices`,
			description: t`On their monthly phone, energy or streaming invoice, the user rounds the amount up and donates the difference to social causes.`,
			cards: [
				radicalSimplicity,
				{ kind: 'media', src: '/redesign/solution-payroll.jpg', alt: t`Someone going through their receipts` },
				totalTransparency,
				{
					kind: 'media',
					src: '/redesign/invoices-thank-you.jpg',
					alt: t`The Worldcoo app thanking a donor for their donation`,
				},
			],
		},
		{
			key: 'portal',
			tab: t`Solidarity Portal`,
			description: t`A web platform tailored to your company. Ideal for emergencies, solidarity challenges or key dates: employees and customers donate from 1 €.`,
			cards: [
				{ kind: 'media', src: '/redesign/portal-laptop.jpg', alt: t`A woman donating from her laptop` },
				{ kind: 'screen', src: PORTAL_PAGE, alt: t`A solidarity portal page` },
				{ kind: 'media', src: '/redesign/portal-team.jpg', alt: t`Colleagues talking in an office` },
				{ kind: 'screen', src: PORTAL_IMPACT, alt: t`A campaign's donation progress and impact figures` },
			],
		},
	];
}
